<template>
  <div class="asset-viewer">
    <div class="asset-viewer__bg touch" @click="close"></div>

    <div v-if="tag && tag.label" class="asset-viewer__label">
      {{ tag.label.en }}
    </div>
    <div class="asset-viewer__close touch" @click="close">
      <fa :icon="['fal', 'times']"/>
    </div>
    <div
      class="asset-viewer__wrapper touch"
      :class="{'asset-viewer__wrapper--expand': expanded}"
    >
      <div v-if="payloadX.length > 1" class="asset-viewer__index">
        {{ payloadIndex + 1 }} / {{ payloadX.length }}
      </div>
      <div class="asset-viewer__expand touch" @click="expanded = !expanded">
        <fa v-if="expanded" :icon="['fal', 'compress-alt']"/>
        <fa v-if="!expanded" :icon="['fal', 'expand-alt']"/>
      </div>
      <div v-if="tag && tag.icon" class="asset-viewer__icon">
        <fa :icon="['fas', tag.icon]"/>
      </div>
      <div v-if="payloadX.length > 1" class="asset-viewer__left touch" @click="indexDown">
        <fa :icon="['fal', 'chevron-left']"/>
      </div>
      <div v-if="payloadX.length > 1" class="asset-viewer__right touch" @click="indexUp">
        <fa :icon="['fal', 'chevron-right']"/>
      </div>
      <transition :name="'fast-fade'" mode="in-out">
        <div
          :key="activePayload"
          class="asset touch"
          @click="openVirtualTour"
        >
          <div v-if="payloadType === 'video'">
            <div
              v-if="status === 'stopped' || status === 'paused'"
              class="play-icon touch"
              @click="play"
            >
              <fa :icon="['fas', 'play']"/>
            </div>
            <div
              v-if="status === 'playing'"
              class="pause-icon touch"
              @click="pause"
            >
              <fa :icon="['fal', 'pause']"/>
            </div>
            <video
              :key="activePayload"
              :src="activePayload"
              playsinline
              ref="video"
              @ended="finished"
              @play="play"
              @timeupdate="progress"
            />
            <div v-if="playbackProgress > 0" class="asset-viewer__progress">
              <div class="progress-bar" :style="`width: ${playbackProgress}%`"/>
            </div>
          </div>
          <div v-if="payloadType === 'image'">
            <img :src="activePayload" alt="">
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import VirtualTour from '@/components/VirtualTour'
import { mapGetters } from 'vuex'

export default {
  props: ['payload', 'tag', 'virtualTourUrl'],

  components: { VirtualTour },

  data () {
    return {
      expanded: false,
      payloadIndex: 0,
      playbackProgress: 0,
      slideTransition: 'fade-slide-right',
      status: 'stopped',
    }
  },

  watch: {
    $route () {
      this.$store.dispatch('actions/TRIGGER_SHOW_ASSET', '')
    },

    payload () {
      this.playbackProgress = 0
    }
  },

  computed: {
    ...mapGetters({
      activeVirtualTourUrl: 'property/activeVirtualTourUrl'
    }),

    activePayload () {
      return this.payloadX[this.payloadIndex]
    },

    payloadType () {
      const ext = this.activePayload.split('.').pop()

      if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
        return 'image'
      }

      if (['mov', 'mp4', 'webm'].includes(ext)) {
        return 'video'
      }

      return null
    },

    payloadX () {
      let payload = this.payload.map(url => this.$store.state.assetsPath + url)
      if (this.virtualTourUrl && this.$store.state.isMatterportAvailable) {
        payload.push('/assets/virtual-tour-slide.jpg')
      }
      return payload
    },
  },

  methods: {
    close () {
      this.$store.dispatch('actions/TRIGGER_SHOW_ASSET', '')
    },

    finished () {
      this.stop()
    },

    play () {
      this.$refs.video.play().then(() => {
        this.$refs.video.play()
        this.status = 'playing'
      })
    },

    pause () {
      this.$refs.video.pause()
      this.status = 'paused'
    },

    stop () {
      this.$refs.video.pause()
      this.$refs.video.currentTime = 0
      this.status = 'stopped'
    },

    progress () {
      const video = this.$refs.video
      
      if(video && !isNaN(video.duration)) {
        this.playbackProgress = video.currentTime / video.duration * 100
      }
    },

    indexDown () {
      this.slideTransition = 'fade-slide-left'

      if (this.payloadType === 'video') {
        this.stop()
      }

      if (this.payloadIndex === 0) {
        this.payloadIndex = this.payloadX.length - 1
      } else {
        this.payloadIndex--
      }
    },

    indexUp () {
      this.slideTransition = 'fade-slide-right'

      if (this.payloadType === 'video') {
        this.stop()
      }

      if (this.payloadIndex === this.payloadX.length - 1) {
        this.payloadIndex = 0
      } else {
        this.payloadIndex++
      }
    },

    openVirtualTour () {
      if (this.virtualTourUrl && this.$store.state.isMatterportAvailable && this.payloadIndex === this.payloadX.length - 1) {
        this.$store.commit('property/SET_ACTIVE_VIRTUAL_TOUR_URL', this.virtualTourUrl)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";
@import "@/styles/_mixins.scss";
@import "@/styles/_animations.scss";

.asset-viewer {
  // background: rgba(#000, .8);
  height: 98rem;
  left: 0;
  position: absolute;
  top: 0;
  width: 160rem;
  z-index: 100;

  &__bg {
    background: rgba(#000, .8);
    height: 98rem;
    left: 0;
    position: absolute;
    top: 0;
    width: 160rem;
    z-index: -1;
  }

  &__icon {
    align-items: center;
    background-color: $action-tag-bg-color;
    border: .35em solid #fff;
    border-radius: $action-tag-border-radius;
    box-shadow: rgba($black, 0.6) 0.1rem 0.2rem 0.4rem;
    color: $action-tag-font-color;
    display: flex;
    font-size: $action-tag-font-size;
    height: 5rem;
    justify-content: center;
    left: 50%;
    position: absolute;
    top: -2rem;
    transform: translateX(-50%);
    width: 5rem;
    z-index: 10;

    svg {
      font-size: 3.8em;
    }
  }

  &__label {
    align-items: center;
    bottom: 14.2rem;
    display: flex;
    font-size: 1.6rem;
    justify-content: center;
    position: absolute;
    left: 50%;
    text-shadow: 0rem 0rem .5rem #000;
    text-transform: uppercase;
    transform: translateX(-50%);
    z-index: 10;
  }

  &__index {
    bottom: 1.25rem;
    left: 1.5rem;
    position: absolute;
    z-index: 10;
  }

  &__close {
    align-items: center;
    background: #cedace;
    border-radius: 50%;
    bottom: 10rem;
    color: #000;
    cursor: pointer;
    display: flex;
    filter: drop-shadow(0.1rem 0.2rem 0.4rem rgba(0, 0, 0, .6));
    height: 3rem;
    justify-content: center;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    width: 3rem;
    z-index: 10;

    svg {
      font-size: 2.8em;
    }
  }

  &__expand {
    align-items: center;
    background: #cedace;
    border-radius: 50%;
    bottom: 2rem;
    color: #000;
    cursor: pointer;
    display: flex;
    filter: drop-shadow(0.1rem 0.2rem 0.4rem rgba(0, 0, 0, .6));
    height: 3rem;
    justify-content: center;
    position: absolute;
    right: .5rem;
    transform: translateX(-50%);
    width: 3rem;
    z-index: 10;

    svg {
      font-size: 2em;
    }
  }

  &__wrapper {
    bottom: 8rem;
    height: 56.25rem;
    left: 50%;
    position: absolute;
    transform: translateX(-50%);
    transition: height 300ms ease-in-out, width 300ms ease-in-out;
    width: 100rem;

    &--expand {
      height: 72.8rem;
      width: 130rem;
    }

    .play-icon {
      font-size: 5rem;
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      z-index: 10;
    }

    .pause-icon {
      bottom: .5rem;
      left: 1rem;
      position: absolute;
      z-index: 10;

      svg {
        filter: drop-shadow(0.1rem 0.2rem 0.4rem rgba(0, 0, 0, .6));
        height: 4rem;
        padding: 1rem;
        width: 4rem;
      }
    }

    img,
    video {
      border-radius: 1rem;
      height: 100%;
      position: absolute;
      object-fit: cover;
      overflow: hidden;
      width: 100%;
    }
  }

  &__progress {
    bottom: 0;
    height: .5rem;
    left: 0;
    position: absolute;
    right: 0;

    .progress-bar {
      background: #eaeaea;
      height: 100%;
      opacity: .75;
      position: relative;
      width: 0;
    }
  }

  &__left {
    align-items: center;
    background: #cedace;
    border-radius: 0 .5rem .5rem 0;
    bottom: 10rem;
    color: #000;
    cursor: pointer;
    display: flex;
    filter: drop-shadow(0.1rem 0.2rem 0.4rem rgba(0, 0, 0, .6));
    height: 6rem;
    justify-content: center;
    left: 0;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 3rem;
    z-index: 10;

    svg {
      font-size: 2.8em;
    }
  }

  &__right {
    align-items: center;
    background: #cedace;
    border-radius: .5rem 0 0 .5rem;
    bottom: 10rem;
    color: #000;
    cursor: pointer;
    display: flex;
    filter: drop-shadow(0.1rem 0.2rem 0.4rem rgba(0, 0, 0, .6));
    height: 6rem;
    justify-content: center;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3rem;
    z-index: 10;

    svg {
      font-size: 2.8em;
    }
  }
}

.asset {
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
}
</style>